import React, { useEffect, useRef, useImperativeHandle, useState, useCallback } from "react";
import { Form } from "../ui/universe";
import InputParams from "../params/InputParams";
import TemplateBar from "../params/InputParams/renderers/TemplateBar";
import { buildSchemaItemsForRequest } from "../params/InputParams/renderers/FieldSchemaEditor";
import DocExtractWelcomeDialog, { shouldShowWelcomeDialog } from "./DocExtractWelcomeDialog";
import docExtractTemplates from "../../const/doc_extract_templates.json";
import { useTemplateStore } from "../../store/templateStore";
import { Button, Radio, Tooltip, Tabs } from "tdesign-react";
import {
  dealEduAgentRect,
  dealMarkInfoRect,
  dealEssayRect,
} from "../../utils/edu.js";
import { baseOcrRequest, addRecentMenu as addRecentMenuApi } from "../../api";
import {
  DownloadExcel,
  DownloadMd,
  DownloadJson,
  resolvePdfPageImagesWithBff,
} from "../../utils/index.js";
import { showErrorMessage } from "../../utils/message.jsx";
import { uploadToCos, needCosUpload } from "../../utils/cosUpload.js";
import { v4 as uuidv4 } from "uuid";
import {
  COS_URL,
  DEFAULT_EDU_AGENT_IMG_PREFIX,
} from "../../const/constants.js";
import ResponseParams from "../params/ResponseParams";
import { useRecursiveAxiosCaller } from "../../hooks";
import {
  useOcrStore,
  useUIStore,
  useImageStore,
  useMenuStore,
} from "../../store";
import {
  getCachedResponse,
  shouldCache,
  storeCachedResponse,
  CACHE_CONFIG,
} from "../../cache/index.js";
import "../../style/GeneralBasicOCR.css";
import "../../style/RecognizeAgent.css";

import {
  REGEX,
  DEFAULT_RESPONSE_TABS,
  MAX_RESULT_TIMES,
  MAX_RESULT_GAP,
} from "../../const/constants.js";

/**
 * GeneralBasicOCR组件：通用OCR识别组件，支持同步和异步OCR识别功能。
 */
export default function GeneralBasicOCR({
  cRef,
  onSelect,
  onChangeImg,
  onChangeImageEnhancement,
  onImageError,
  onResult,
  onSubmitResult,
}) {
  // 从 menuStore 获取当前菜单和操作
  const detail = useMenuStore((state) => state.currentMenu);
  const addRecentMenu = useMenuStore((state) => state.addRecentMenu);

  // 从 ocrStore 获取状态（使用选择器确保正确订阅）
  const paramsValue = useOcrStore((state) => state.paramsValue);
  const requestParamsValue = useOcrStore((state) => state.requestParamsValue);
  const paramsList = useOcrStore((state) => state.paramsList);
  const smartKeyList = useOcrStore((state) => state.smartKeyList);
  const apiType = useOcrStore((state) => state.apiType);
  const request = useOcrStore((state) => state.request);
  const response = useOcrStore((state) => state.response);
  const rspTabs = useOcrStore((state) => state.rspTabs);
  const currentTab = useOcrStore((state) => state.currentTab);
  const rectOptions = useOcrStore((state) => state.rectOptions);
  const showParams = useOcrStore((state) => state.showParams);
  // 从 ocrStore 获取操作函数
  const {
    setParamsValue,
    setRequestParamsValue,
    setParamsList,
    setSmartKeyList,
    addSmartKey,
    updateSmartKey,
    removeSmartKey,
    setApiType,
    setRequest,
    setResponse,
    clearRequestResponse,
    setRspTabs,
    setCurrentTab,
    setRectOptions,
    setEduMarkData,
    setShowParams,
  } = useOcrStore();

  // 从 uiStore 获取状态（使用选择器确保正确订阅）
  const isLoading = useUIStore((state) => state.isLoading);
  const isThinking = useUIStore((state) => state.isThinking);
  const isMarkLoading = useUIStore((state) => state.isMarkLoading);
  // 从 uiStore 获取操作函数
  const { setIsLoading, setIsThinking, setIsMarkLoading, stopLoading } =
    useUIStore();

  // 从 templateStore 获取操作函数
  const { selectTemplate } = useTemplateStore();

  // 从 imageStore 获取状态（使用选择器确保正确订阅）
  const uploadImgList = useImageStore((state) => state.uploadImgList);
  const image = useImageStore((state) => state.currentImage);
  const clipImg = useImageStore((state) => state.clipImg);
  const totalPage = useImageStore((state) => state.totalPage);

  const messagesEnd = useRef(null);
  const paramsRef = useRef(null);

  // 轮询流程代际令牌：每发起一次识别流程 +1。
  // 用于识别并丢弃「过期流程」(用户已切到别的图片)的轮询事件 / 异步回填，
  // 避免过期的 onComplete('stopped') 执行 stopLoading() 把新流程的 loading 状态清掉。
  const flowIdRef = useRef(0);

  // 参数面板拖拽调整高度
  const [paramsPanelHeight, setParamsPanelHeight] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartY = useRef(0);
  const resizeStartHeight = useRef(0);

  // 文档抽取多模态欢迎弹窗
  const [welcomeDialogVisible, setWelcomeDialogVisible] = useState(false);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartY.current = e.clientY;
    // 读取参数面板当前高度作为起始值
    if (paramsRef.current) {
      resizeStartHeight.current = paramsRef.current.getBoundingClientRect().height;
    }
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    let rafId = null;

    const handleMouseMove = (e) => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const deltaY = e.clientY - resizeStartY.current;
        const newHeight = resizeStartHeight.current + deltaY;
        // 限制最小 100px，最大不超过容器的 70%
        const containerHeight = paramsRef.current?.parentElement?.getBoundingClientRect().height || 600;
        const clampedHeight = Math.max(100, Math.min(newHeight, containerHeight * 0.7));
        // 直接操作 DOM 避免 React 重渲染
        if (paramsRef.current) {
          paramsRef.current.style.maxHeight = clampedHeight + 'px';
          paramsRef.current.style.height = clampedHeight + 'px';
        }
        rafId = null;
      });
    };

    const handleMouseUp = (e) => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      // 同步最终高度到 state
      if (paramsRef.current) {
        const finalHeight = paramsRef.current.getBoundingClientRect().height;
        setParamsPanelHeight(finalHeight);
      }
      setIsResizing(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  const { start, stop, on } = useRecursiveAxiosCaller(
    MAX_RESULT_TIMES,
    MAX_RESULT_GAP,
    (res) => {
      return res?.JobStatus === "DONE";
    },
  );

  // 结果所属图片已不是当前展示的图片时（典型场景：切图后旧请求才返回），
  // 不写入结果面板，避免旧结果把当前图片的结果覆盖掉。
  // 左侧预览有同样语义的 isStaleImageResult 守卫（OcrLayout）
  const setResponseIfCurrent = (res, resultImage) => {
    const liveCurrentImageUrl = useImageStore.getState().currentImage?.url;
    const resultImageUrl = resultImage?.url;
    if (resultImageUrl && liveCurrentImageUrl && resultImageUrl !== liveCurrentImageUrl) {
      return;
    }
    setResponse(res);
  };

  useImperativeHandle(cRef, () => ({
    start: ({ image: img, config }) => {
      const _smartKeyList = smartKeyList.filter((item) => !item.isCustom);
      setSmartKeyList(_smartKeyList);

      // 从 store 读取最新的 paramsValue，避免 closure 中旧值覆盖 useEffect 设置的初始值
      const currentParamsValue = useOcrStore.getState().paramsValue;
      const currentRequestParamsValue =
        useOcrStore.getState().requestParamsValue;
      let _requestParams = currentRequestParamsValue;

      // RecognizeAgent：新上传图片时保持参数调整区域不变（模型、任务类型、填入的参数等）
      // 仅当存在 config（如示例图片 defaultConfig）时才覆盖
      if (config) {
        // 如果有传入 config（如示例图片的 defaultConfig），合并到请求参数
        _requestParams = {
          ...currentParamsValue,
          ...config,
        };
        setRequestParamsValue(_requestParams);
        // 同时更新显示参数（因为 config 可能包含新的参数值）
        setParamsValue(_requestParams);
      }

      // 每次重新上传图片，将页码置为初始值
      if (_requestParams.hasOwnProperty("FileEndPageNumber")) {
        _requestParams.FileEndPageNumber = 1;
      }
      if (_requestParams.hasOwnProperty("FileStartPageNumber")) {
        _requestParams.FileStartPageNumber = 1;
      }

      handleStart({ _params: _requestParams, _image: img, _smartKeyList, skipValidation: true });
    },
  }));

  // 注册事件监听器
  useEffect(() => {
    const cleanupFns = [];

    cleanupFns.push(
      on("onResponse", (res, attempt, log, extra) => {
        // 过期流程（用户已切走）的轮询响应直接丢弃，不写结果 / 不动 loading
        const flowId = extra?.flowId;
        if (flowId != null && flowId !== flowIdRef.current) {
          console.warn('[切题调试] onResponse 忽略过期流程', {
            flowId,
            current: flowIdRef.current,
            JobStatus: res?.JobStatus,
          });
          return;
        }
        setResponseIfCurrent(res, extra?.requestParams?.image || extra?.image);

        if (res?.Error || res?.error || res?.JobStatus === "FAIL") {
          handleStop();
          setIsThinking(false);
          setIsLoading(false);
          showErrorMessage(res.message || res.msg);
        }
      }),
    );

    cleanupFns.push(
      on("onComplete", (result, reason, _, extraData) => {
        // 过期流程的完成/停止事件直接丢弃：关键是不能让它执行 stopLoading()，
        // 否则会把用户切换后新流程刚设置的 isThinking / isLoading 清掉，
        // 导致右侧「正在切题」loading 消失
        const flowId = extraData?.flowId;
        if (flowId != null && flowId !== flowIdRef.current) {
          console.warn('[切题调试] onComplete 忽略过期流程', {
            flowId,
            current: flowIdRef.current,
            reason,
          });
          return;
        }
        const liveUrl = useImageStore.getState().currentImage?.url;
        const resultUrl =
          extraData?.requestParams?.image?.url || extraData?.image?.url;
        console.warn('[切题调试] onComplete', {
          reason,
          flowId,
          resultUrl,
          liveUrl,
          isStale: !!(resultUrl && liveUrl && resultUrl !== liveUrl),
        });
        handleStop();
        switch (reason) {
          case "completed":
            successHandler(result, extraData);
            setIsThinking(false);
            // markEdu 的 isMarkLoading 已在 successHandler 中处理
            break;
          case "stopped":
            stopLoading();
            break;
          case "max_attempts":
          case "error":
            stopLoading();
            showErrorMessage(result?.message || result?.msg);
            break;
          default:
            stopLoading();
        }
      }),
    );

    return () => cleanupFns.forEach((fn) => fn());
  }, [on]);

  const scrollToBottom = () => {
    if (messagesEnd?.current) {
      messagesEnd.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  };

  useEffect(() => {
    if (smartKeyList?.length > 0) {
      scrollToBottom();
    }
  }, [smartKeyList]);

  useEffect(() => {
    if (clipImg) {
      handleStartEduAgent({ clipImg });
    }
  }, [clipImg]);

  /**
   * 条件判断：condition: { key, value?, notEmpty? }
   * - value：当前值命中给定值（或数组中的任一值）
   * - notEmpty：依赖字段有值（非空字符串/非空数组/非 null）
   */
  const isConditionMet = (condition) => {
    if (!condition) return true;
    const current = paramsValue?.[condition.key];
    if (condition.notEmpty) {
      return Array.isArray(current)
        ? current.length > 0
        : current !== undefined && current !== null && current !== '';
    }
    if (condition.value === undefined) return true;
    const expectList = Array.isArray(condition.value) ? condition.value : [condition.value];
    return expectList.includes(current);
  };

  // 参数显隐：showWhen 不满足时隐藏
  const isParamsVisible = (param) => isConditionMet(param?.showWhen);

  // 参数禁用：disableWhen 不满足时置灰但保留展示
  const isParamsDisabled = (param) => !isConditionMet(param?.disableWhen);

  const handleResetParams = () => {
    handleStop();
    setParamsAndValue(detail.defaultApi, false, false);
    setSmartKeyList([]);
    selectTemplate(''); // 清空模板选择器
    if (detail.actionType === "eduAgent" && rectOptions.length > 0) {
      onSelect(rectOptions[0].label, rectOptions[0].key, rectOptions[0]);
    }
  };

  const setParamsAndValue = (
    apitype,
    startImmediately = true,
    keepOldVal = false,
  ) => {
    const _value = {};
    let _paramsList = [];
    let _tab = detail.config?.Tabs || DEFAULT_RESPONSE_TABS;
    setApiType(apitype);

    if (apitype === "sync" && detail.syncConfig) {
      _paramsList = detail.syncConfig.paramsList;
      _tab = detail.syncConfig.Tabs || DEFAULT_RESPONSE_TABS;
    } else if (apitype === "async" && detail.asyncConfig) {
      _paramsList = detail.asyncConfig.paramsList;
    } else {
      _paramsList = detail.paramsList;
    }

    setParamsList(_paramsList);
    if (_paramsList?.length > 0) {
      _paramsList.forEach((e) => {
        if (paramsValue.hasOwnProperty(e.key) && keepOldVal) {
          _value[e.key] = paramsValue[e.key];
        } else {
          _value[e.key] = JSON.parse(JSON.stringify(e.default));
        }
      });
    }

    setParamsValue({ ..._value });
    setRequestParamsValue({ ..._value });
    setRspTabs(_tab);
    setCurrentTab(_tab[0].id);

    if (startImmediately) {
      handleStart({
        _params: _value,
        _image: image,
        _smartKeyList: [],
        _apiType: apitype,
      });
    }
  };

  useEffect(() => {
    if (!detail) return;
    clearRequestResponse();
    // 切换菜单时不立即调用接口，由 OcrLayout 的 setConfig 统一触发
    // 避免使用旧图片调用新接口的问题
    setParamsAndValue(detail.defaultApi, false, false);
    setSmartKeyList([]);

    // 切换到 ExtractDocMulti 时，若未关闭过欢迎弹窗则显示
    if (detail.action === 'ExtractDocMulti' && shouldShowWelcomeDialog()) {
      setWelcomeDialogVisible(true);
    } else {
      setWelcomeDialogVisible(false);
    }
  }, [detail]);

  const successHandler = async (res, extra) => {
    console.warn('[切题调试] successHandler 进入', {
      flowId: extra?.flowId,
      currentFlowId: flowIdRef.current,
      fromCache: !!extra?.fromCache,
      imageUrl: extra?.requestParams?.image?.url,
      isThinking: useUIStore.getState().isThinking,
      isLoading: useUIStore.getState().isLoading,
    });
    handleStop();
    setResponseIfCurrent(res, extra?.requestParams?.image);
    let rect = [];
    const pdfNum = extra.pdfNum;
    // Hoist mergedData so it's accessible in onResult below
    let mergedData = null;

    // PDF 场景：提交响应里的 ImageZipUrl 是「PDF 转页后的图片压缩包」，
    // 由 BFF 下载并转存到我们自己的 COS 后取回逐页图片，供左侧按页预览与坐标叠加
    const sourceFileType = extra.requestParams?.image?.fileType;
    // 前端已本地渲染好 PDF 逐页图时，无需再取后端压缩包
    const hasLocalPdfPages =
      Array.isArray(extra.requestParams?.image?.pdfImgList) &&
      extra.requestParams.image.pdfImgList.length > 0;
    let auditData = extra.auditData || [];
    if (
      extra.pdfZipUrl &&
      auditData.length === 0 &&
      sourceFileType === "pdf" &&
      !hasLocalPdfPages
    ) {
      const pdfPages = await resolvePdfPageImagesWithBff(extra.pdfZipUrl);
      if (pdfPages.length > 0) {
        auditData = pdfPages;
      }
    }

    // 补上每一页在后端坐标系里的宽高：预览层用 imgRadio = 首页高 / 本页高
    // 换算坐标框，缺了会导致第 2 页起框位偏移
    if (auditData.length > 0 && res?.QuestionInfo?.length > 0) {
      auditData = auditData.map((page, i) => ({
        ...page,
        Height: page.Height || res.QuestionInfo[i]?.Height,
        Width: page.Width || res.QuestionInfo[i]?.Width,
      }));
    }

    if (extra.config.actionType === "markEdu") {
      // 更新 eduMarkData，合并原始提交结果和轮询结果
      mergedData = {
        ...(extra.resSubmit || {}),
        ...res,
      };

      console.log(
        "[markEdu] 第二阶段 (Describe轮询完成) mergedData:",
        JSON.stringify(
          {
            Angle: mergedData?.QuestionInfo?.[0]?.Angle,
            Width: mergedData?.QuestionInfo?.[0]?.Width,
            Height: mergedData?.QuestionInfo?.[0]?.Height,
            OrgWidth: mergedData?.OrgWidth,
            OrgHeight: mergedData?.OrgHeight,
            hasBase64: !!mergedData?.QuestionInfo?.[0]?.ImageBase64,
          },
          null,
          2,
        ),
      );

      setEduMarkData(mergedData);
      // 在设置数据后立即取消 loading，确保在同一个 React 更新批次中
      setIsMarkLoading(false);

      // markEdu 需要存储合并后的数据到缓存（包含 QuestionInfo 和 MarkInfos）
      // 因为 useRecursiveAxiosCaller 只会存储轮询结果，不包含提交时的 QuestionInfo
      // 注意：如果是缓存命中（extra.fromCache），则不需要再存储
      const cacheRequestConfig = extra.originalRequestConfig;
      if (
        !extra.fromCache &&
        cacheRequestConfig &&
        shouldCache(cacheRequestConfig, CACHE_CONFIG)
      ) {
        try {
          // 使用当前时间作为缓存时间戳（最后获取到结果的时间）
          const cacheTimestamp = Date.now();
          await storeCachedResponse(
            cacheRequestConfig,
            mergedData,
            CACHE_CONFIG,
            cacheTimestamp,
          );
          console.log(
            `[OCR Cache] markEdu 合并数据已缓存: ${cacheRequestConfig.cmd}`,
          );
        } catch (cacheError) {
          console.error("[OCR Cache] markEdu 缓存存储失败:", cacheError);
        }
      }

      const rect1 = dealEduAgentRect(extra.resSubmit, false, pdfNum);
      const rect2 = dealMarkInfoRect(res, extra.resSubmit, pdfNum);
      rect = [...rect1, ...rect2];
      if (rect.length > 0 && rect[0]) {
        onSelect(rect[0].label, rect[0].key, rect[0]);
      }
    } else if (extra.config.resultConfig?.type === "essayEdu") {
      rect = dealEssayRect(res, 0, 0, pdfNum);
    }

    onResult &&
      onResult({
        uploadImglist: extra.uploadImglist,
        config: extra.config,
        ...extra.requestParams,
        rect,
        rsp: {
          // For markEdu, use mergedData so QuestionInfo (with Width/Height) is available
          // for imgSize calculation in OcrLayout. For other types, res is sufficient.
          ...(extra.config.actionType === "markEdu" ? mergedData : res),
          auditData,
        },
      });
    setIsLoading(false);
  };

  const errorHandler = (res, extra) => {
    showErrorMessage(res.message || res.msg);
    setResponseIfCurrent(res, extra?.requestParams?.image);
    onResult &&
      onResult({
        uploadImglist: extra.uploadImglist,
        isError: true,
        config: extra.config,
        ...extra.requestParams,
        rsp: {
          ...res,
          auditData: extra.auditData,
        },
      });
    stopLoading();
  };

  const handleStop = async () => {
    stop();
  };

  const handleAddRecentMenu = async (InterfaceName) => {
    try {
      const res = await addRecentMenuApi({ InterfaceName });
      if (!res.error) {
        addRecentMenu(InterfaceName);
      }
    } catch (e) {
      // ignore error
    }
  };

  const handleStartEduAgent = async ({ clipImg: clipImgData }) => {
    if (!clipImgData) return;
    setIsThinking(true);
    clearRequestResponse();

    const payload = {
      ImageBase64: clipImgData.img,
    };

    const res = await baseOcrRequest({
      cmd: detail.actionList[1],
      data: payload,
      isExample: clipImgData.img?.startsWith(DEFAULT_EDU_AGENT_IMG_PREFIX),
      fileType: "png",
      module: detail.module || "ocr",
    });

    handleAddRecentMenu(detail.actionList[1]);
    setIsThinking(false);
    setRequest({ ...payload });

    if (res.error) {
      showErrorMessage(res.message || res.msg);
    }
    setResponse(res);
  };

  const handleStart = async ({
    _params: inputParams,
    _image = image,
    _smartKeyList = smartKeyList,
    _apiType = apiType,
    skipValidation = false,
  } = {}) => {
    if (!_image) return;
    if (!validate(_smartKeyList)) return;


    // 关键：创建副本，避免后续 delete/赋值操作污染 store state
    // inputParams 默认值为 undefined，会回退到 requestParamsValue
    const _params = { ...(inputParams || requestParamsValue) };

    // RecognizeAgent 参数校验（仅在用户主动触发时校验，切换图片时跳过）
    if (!skipValidation && detail?.action === 'RecognizeAgent') {
      const queryType = _params.QueryType;
      const query = _params.Query || [];
      if (queryType === 1 && (!query.length || !query[0])) {
        showErrorMessage('请输入判断内容');
        return;
      }
      if (queryType === 1 && query.length > 1) {
        showErrorMessage('判断内容数量仅为1');
        return;
      }
      if (queryType === 2) {
        if (!query.length) {
          showErrorMessage('请输入分类项');
          return;
        }
        if (query.length < 2 || query.length > 5) {
          showErrorMessage('分类项数量必须在2-5个之间');
          return;
        }
      }
      if (queryType === 3 && (!query.length || !query[0])) {
        showErrorMessage('请输入总结主题');
        return;
      }
      if (queryType === 3 && query.length > 1) {
        showErrorMessage('总结主题数量仅为1');
        return;
      }
      if (queryType === 4) {
        const schemaItems = _params.SchemaItems || [];
        if (!schemaItems.length) {
          showErrorMessage('请至少添加一个提取字段');
          return;
        }
      }
    }

    handleStop();
    clearRequestResponse();

    // 为本次识别流程分配新的代际令牌。此后旧流程(上一张图)的任何轮询事件、
    // 异步提交回填都会因 flowId 不匹配而被丢弃，互不干扰。
    const myFlowId = ++flowIdRef.current;
    console.warn('[切题调试] handleStart', {
      myFlowId,
      imageUrl: _image?.url,
      fileType: _image?.fileType,
      apiType: _apiType,
      action: detail?.action,
      isThinking: useUIStore.getState().isThinking,
      isLoading: useUIStore.getState().isLoading,
    });

    // isThinking / isMarkLoading 是跨流程共享的全局 loading 标志，过去只在 onComplete 里重置。
    // 由于现在会丢弃「过期流程」的 onComplete，旧流程的 isThinking 不会被它重置；
    // 缓存命中路径也不走 onComplete。为避免上一张图的 loading 状态残留/泄漏，
    // 这里统一归零，由下面各分支按本次流程的真实情况重新设置（轮询→true，缓存命中→保持 false）。
    // 归零瞬间若是 Async 分支会立刻 setIsLoading(true)，同一渲染批次内 loading 不会闪断。
    setIsThinking(false);
    setIsMarkLoading(false);

    let config = { ...detail };

    if (_apiType === "sync" && detail.syncConfig) {
      config = { ...detail, ...detail.syncConfig };
    } else if (_apiType === "async" && detail.asyncConfig) {
      config = { ...detail, ...detail.asyncConfig };
    } else {
      config = { ...detail, ...detail.asyncConfig };
    }

    // 过滤掉 excludeFromRequest: true 的参数，不发送到后端
    // 注意：操作的是副本，不会影响 store state
    const _currentParamsList = config.paramsList || [];

    // 高级配置开启时，检查 ItemNames 中是否有配置了 GT 值或审核 prompt 的字段
    // 如果有，则在入参中添加 EnableAudit: true，通知后端开启审核
    const advancedConfigParam = _currentParamsList.find((item) => item.key === 'advancedConfig');
    if (advancedConfigParam && _params[advancedConfigParam.key]) {
      const itemNames = _params.ItemNames || [];
      const hasAuditField = Array.isArray(itemNames) && itemNames.some(
        (item) => item.KeyExpectedValue || item.KeyAuditPrompt
      );
      if (hasAuditField) {
        _params.EnableAudit = true;
      }
    }

    // ExtractDocMulti：将 NewItemNames 作为实际发送字段（优先于旧 ItemNames）
    // 同时清理不需要发送的 NewItemNames key（后端使用 NewItemNames 参数名）
    if (config.action === 'ExtractDocMulti') {
      const newItemNames = _params.NewItemNames;
      if (Array.isArray(newItemNames) && newItemNames.length > 0) {
        // NewItemNames 有值时优先使用，旧 ItemNames 不发送
        delete _params.ItemNames;
      } else {
        // NewItemNames 为空时不发送该字段
        delete _params.NewItemNames;
      }
    }

    const _excludeKeys = new Set(
      _currentParamsList
        .filter((item) => item.excludeFromRequest)
        .map((item) => item.key)
    );
    _excludeKeys.forEach((key) => {
      delete _params[key];
    });

    // VerifyScenePhoto：鉴伪模式固定为图像鉴伪（Image），不在页面上展示该参数
    if (config.action === 'VerifyScenePhoto' && _params.Mode === undefined) {
      _params.Mode = 'Image';
    }

    // 可选参数留空时不下发（空字符串/空数组/null 视为未填写；false 属有效值需保留）
    _currentParamsList
      .filter((item) => item.omitWhenEmpty)
      .forEach((item) => {
        const val = _params[item.key];
        if (Array.isArray(val)) {
          // 数组参数剔除空项后再判断是否下发
          const cleaned = val.filter(
            (v) => v !== '' && v !== null && v !== undefined
          );
          if (cleaned.length === 0) delete _params[item.key];
          else _params[item.key] = cleaned;
          return;
        }
        const isEmpty =
          val === '' || val === null || val === undefined;
        if (isEmpty) delete _params[item.key];
      });

    // 自定义推理：未填写推理规则时，ReasoningPrompt 与同组推理配置（ReasoningConfig）一并不下发
    // 后端约定「传入 ReasoningPrompt 即开启推理流程」，因此空值必须剔除而不是发送空串
    const reasoningPromptParam = _currentParamsList.find(
      (item) => item.key === 'ReasoningPrompt'
    );
    const reasoningPromptText =
      typeof _params.ReasoningPrompt === 'string' ? _params.ReasoningPrompt.trim() : '';
    if (reasoningPromptParam) {
      if (!reasoningPromptText) {
        const configKey = reasoningPromptParam.groupKey || 'ReasoningConfig';
        delete _params.ReasoningPrompt;
        _currentParamsList
          .filter((item) => item.configKey === configKey)
          .forEach((item) => delete _params[item.key]);
      } else {
        // enum 仅提交枚举集合，string 仅提交文本长度，未选输出模式时两者都不提交
        _params.ReasoningPrompt = reasoningPromptText;
        if (_params.OutputMode === 'enum') {
          delete _params.MaxLength;
        } else if (_params.OutputMode === 'string') {
          delete _params.EnumValues;
        } else {
          delete _params.EnumValues;
          delete _params.MaxLength;
        }
      }
    }

    if (config?.showCustomItem || config.resultConfig?.showCustomItem) {
      if (_smartKeyList?.length > 0) {
        _params.ItemNames = _smartKeyList
          .map((i) => {
            return i.isCustom ? detail.options[i.value] : i.value;
          })
          .filter((n) => n);
      } else {
        _params.ItemNames = [];
      }
    }

    if (config.actionType === "eduAgent") {
      setIsLoading(true);
      setRectOptions([]);

      const payload = JSON.parse(JSON.stringify(config.actionList[0].params));
      const resSubmit = await baseOcrRequest({
        cmd: config.actionList[0].action,
        data: {
          ...payload,
          [_image.prefix]: _image.param ? _image.param : _image.url,
        },
        isExample: _image.isExample,
        fileType: _image.fileType,
        module: config.module || "ocr",
      });

      handleAddRecentMenu(config.actionList[1]);
      onImageError(resSubmit.isImageError || false);

      if (resSubmit.error) {
        setIsLoading(false);
        errorHandler(resSubmit, {
          uploadImglist: uploadImgList,
          config: { ...detail, action: detail.actionList[0].action },
          requestParams: { image: _image, requestParams: _params },
          auditData: resSubmit.auditData,
        });
        setRequest({ ..._params, [_image.prefix]: _image.url });
      } else {
        const rect = dealEduAgentRect(resSubmit, true, _params.PdfPageNumber);
        setRectOptions(rect);
        setEduMarkData(resSubmit);
        setIsLoading(false);
        onResult &&
          onResult({
            uploadImglist: uploadImgList,
            config: { ...detail, action: detail.actionList[0].action },
            image: _image,
            requestParams: _params,
            rsp: resSubmit,
            select: rect,
            rect: rect,
          });
      }
    } else if (config.actionType === "Async") {
      setIsLoading(true);

      const payload = JSON.parse(JSON.stringify(_params));
      if (!payload.PdfPageNumber) {
        delete payload.PdfPageNumber;
      }

      const dataParams = { ...payload };
      // 优先处理 multiUpload 多图批次：一次性拼装 List 入参，取单个 JobId
      // 示例图片场景（无 multiPages）也走 List 入参，把当前 image 作为单元素列表
      const hasMultiPages =
        Array.isArray(_image.multiPages) && _image.multiPages.length > 0;
      const isMultiBatch = !!config.multiUpload;

      if (isMultiBatch) {
        const pages = hasMultiPages
          ? _image.multiPages
          : [
              {
                type: _image.type || (_image.isExample ? "url" : undefined),
                OriginUrl: _image.param || _image.OriginUrl || _image.url,
                url: _image.url,
              },
            ];
        const isUrlBatch = pages[0].type === "url" || _image.isExample;
        const listKey = isUrlBatch
          ? `${config.prefix || "Image"}UrlList`
          : `${config.prefix || "Image"}Base64List`;
        dataParams[listKey] = pages.map((p) => p.OriginUrl || p.url);
      } else if (config.supportImgList) {
        dataParams[`${_image.prefix}List`] = _image.param
          ? [_image.param]
          : [_image.url];
      } else {
        dataParams[`${_image.prefix}`] = _image.param
          ? _image.param
          : _image.url;
      }

      // multiUpload 模式下，Submit/Describe 都走真实通道（demo 通道未开放 Describe*Job）
      const effectiveIsExample = isMultiBatch ? false : _image.isExample;

      // 构建原始提交请求配置，用于缓存 key
      const originalRequestConfig = {
        cmd: config.actionList[0],
        data: dataParams,
        isExample: effectiveIsExample,
        fileType: _image.fileType,
        module: config.module || "ocr",
        paramsList: config.paramsList,
      };

      // 先检查缓存，如果命中则直接使用缓存结果，跳过提交和轮询
      if (shouldCache(originalRequestConfig, CACHE_CONFIG)) {
        const cachedResponse = await getCachedResponse(
          originalRequestConfig,
          CACHE_CONFIG,
        );
        if (cachedResponse) {
          console.log(`[OCR Cache] 异步接口缓存命中: ${config.actionList[0]}`);
          // 查缓存是异步的，期间用户可能已切走 → 过期则直接丢弃，不回填结果
          if (myFlowId !== flowIdRef.current) {
            console.warn('[切题调试] Async 缓存命中但流程已过期，丢弃', {
              myFlowId,
              current: flowIdRef.current,
            });
            return;
          }
          console.warn('[切题调试] Async 缓存命中，直接 successHandler（不会轮询/不设 isThinking）', {
            imageUrl: _image?.url,
            myFlowId,
          });
          setRequest({ ..._params, [_image.prefix]: _image.url });
          successHandler(cachedResponse, {
            config,
            requestParams: { image: _image, requestParams: _params },
            uploadImglist: uploadImgList,
            flowId: myFlowId,
          });
          return;
        }
      }

      const resSubmit = await baseOcrRequest({
        ...originalRequestConfig,
        skipCacheStore: true, // 提交请求不缓存，等轮询成功后统一缓存
      });

      // 提交是异步的，期间用户可能已切到别的图片并发起了新流程。
      // 此时本次提交结果作废：既不能发起轮询（会抢占共享轮询实例、污染当前预览），
      // 也不能回写 loading / 结果状态，直接放弃。
      if (myFlowId !== flowIdRef.current) {
        console.warn('[切题调试] Async 提交返回但流程已过期，放弃轮询', {
          myFlowId,
          current: flowIdRef.current,
          imageUrl: _image?.url,
        });
        return;
      }

      handleAddRecentMenu(config.actionList[0]);
      onImageError(resSubmit.isImageError || false);

      const JobId =
        config.supportImgList && !isMultiBatch
          ? resSubmit.JobIds?.[0]
          : resSubmit.JobId;

      if (resSubmit.error || !JobId) {
        setIsLoading(false);
        errorHandler(resSubmit, {
          config,
          uploadImglist: uploadImgList,
          requestParams: { image: _image, requestParams: _params },
          auditData: resSubmit.auditData,
          flowId: myFlowId,
        });
        setRequest(
          isMultiBatch ? { ..._params, ...dataParams } : { ..._params, [_image.prefix]: _image.url }
        );
      } else {
        // PDF：前端已本地渲染出逐页图时，完全不需要去取后端压缩包
        // （对方 COS 不支持跨域，取了也是失败）
        const hasLocalPdfPages =
          Array.isArray(_image.pdfImgList) && _image.pdfImgList.length > 0;

        // 没有本地页图时，才走 BFF 转存/解压兜底，让轮询期间也有页图可看
        let resolvedPdfPages = [];
        if (
          !hasLocalPdfPages &&
          resSubmit.ImageZipUrl &&
          _image.fileType === "pdf"
        ) {
          resolvedPdfPages = await resolvePdfPageImagesWithBff(
            resSubmit.ImageZipUrl,
          );
          if (resolvedPdfPages.length > 0 && onSubmitResult) {
            onSubmitResult({
              image: _image,
              config,
              pdfPages: resolvedPdfPages,
            });
          }
        }

        const startPayload = {
          cmd: config.actionList[1],
          data: { JobId },
          isExample: effectiveIsExample,
          fileType: _image.fileType,
          module: config.module || "ocr",
        };

        start(startPayload, {
          config,
          requestParams: { image: _image, requestParams: _params },
          uploadImglist: uploadImgList,
          auditData:
            resSubmit.auditData?.length > 0
              ? resSubmit.auditData
              : resolvedPdfPages,
          // PDF 会返回转页图片压缩包地址，用于左侧分页预览；
          // 已有本地渲染页图、或已提前解压过，就不再去碰对方 COS
          pdfZipUrl:
            hasLocalPdfPages || resolvedPdfPages.length > 0
              ? ""
              : resSubmit.ImageZipUrl,
          originalRequestConfig, // 传递原始请求配置用于缓存
          flowId: myFlowId,
        });

        console.warn('[切题调试] Async 开始轮询，setIsThinking(true)', {
          imageUrl: _image?.url,
          JobId,
        });
        setIsLoading(false);
        setIsThinking(true);
        setRequest(
          isMultiBatch ? { ..._params, ...dataParams } : { ..._params, [_image.prefix]: _image.url }
        );
      }
    } else if (config.actionType === "markEdu") {
      const payload = JSON.parse(JSON.stringify(_params));
      if (!payload.PdfPageNumber) {
        delete payload.PdfPageNumber;
      }

      // 根据 EnableImageCrop 参数决定是否先调用图像切边增强
      let finalImage = _image;
      const enableImageCrop = _params.EnableImageCrop;

      // 从 payload 中移除 EnableImageCrop，不发送到后端
      delete payload.EnableImageCrop;

      // 只看主图张数决定入参形态（不再区分 url / base64 类型）：
      //   >1 张 → ImageBase64List，=1 张 → 单值 ImageBase64 / ImageUrl
      const multiPages = Array.isArray(_image.multiPages) ? _image.multiPages : [];
      const pageCount = multiPages.length > 0 ? multiPages.length : 1;
      // 整页辅助批改（AssistMarkType=2/3）：ImageBase64List 已被「标准答案图」占用，
      // 且后端要求待批改主图必须走单值 ImageUrl/ImageBase64，
      // 故这类场景主图恒为单值（多页时取第 1 页），不走 List。
      const isWholePageAssist =
        _params.AssistMarkType === 2 || _params.AssistMarkType === 3;
      const useList = !isWholePageAssist && pageCount > 1;

      // 开启矫正的话，先调用图像切边增强
      // （多图批次逐张增强成本高且只返回单图，这里跳过，交给后端处理）
      if (enableImageCrop && !useList) {
        try {
          console.log('开启EnableImageCrop');
          const enhanceRes = await baseOcrRequest({
            cmd: 'CropEnhanceImageOCR',
            data: {
              [_image.prefix]: _image.param ? _image.param : _image.url,
              Crop: 1,           // 开启切边
              Deskew: 1,         // 开启弯曲矫正
              OnlyPosition: 0,   // 返回处理后的图片和坐标
              EnhanceType: -1,   // 不处理增强（保持原色）
              AdjustOrientation: 1 // 调整方向
            },
            isExample: false,    // CropEnhanceImageOCR 是通用接口，不走示例图片通道
            fileType: _image.fileType,
            module: config.module || 'ocr'
          });

          if (!enhanceRes.error && (enhanceRes.CroppedImageUrl || enhanceRes.CroppedImage)) {
            if (enhanceRes.CroppedImageUrl) {
              finalImage = {
                ..._image,
                url: enhanceRes.CroppedImageUrl,
                previewUrl: enhanceRes.CroppedImageUrl,
                prefix: 'ImageUrl',
                isExample: false,
              };
            } else {
              finalImage = {
                ..._image,
                url: `data:image/png;base64,${enhanceRes.CroppedImage}`,
                previewUrl: `data:image/png;base64,${enhanceRes.CroppedImage}`,
                prefix: 'ImageBase64',
                isExample: false,
              };
            }
          } else {
            console.warn('图像增强失败');
          }
        } catch (e) {
          console.error('[markEdu]',e);
        }
      } else if (enableImageCrop) {
        console.log('[markEdu] 多图批次跳过前端切边增强，直接以 List 提交');
      } else {
        console.log('[markEdu] EnableImageCrop=false');
      }

      // 组装提交入参：是否用 List 只看主图张数；List 的 key 名按图片类型区分
      //   url 批次 → ImageUrlList，base64 批次 → ImageBase64List
      let dataParams;
      if (useList) {
        const isUrlList = multiPages[0]?.type === 'url';
        const listKey = isUrlList
          ? `${config.prefix || 'Image'}UrlList`
          : `${config.prefix || 'Image'}Base64List`;
        dataParams = {
          ...payload,
          [listKey]: multiPages.map((p) => p.param || p.OriginUrl || p.url),
        };
      } else {
        // 单图：Type=2/3 多页辅助批改时主图取第 1 页作为单值（ImageBase64List 留给答案图）
        const single = multiPages.length > 0 ? multiPages[0] : finalImage;
        const singlePrefix =
          multiPages.length > 0 ? single.prefix : finalImage.prefix;
        dataParams = {
          ...payload,
          // 用户开启矫正的话，使用增强图片接口返回的图片作为参数
          [singlePrefix]: single.param ? single.param : single.url,
        };
      }

      // 构建原始提交请求配置，用于缓存 key
      const originalRequestConfig = {
        cmd: config.actionList[0],
        data: dataParams,
        isExample: finalImage.isExample,
        fileType: finalImage.fileType,
        module: config.module || 'ocr',
        paramsList: config.paramsList
      };

      // 先检查缓存，如果命中则直接使用缓存结果，跳过提交和轮询
      if (shouldCache(originalRequestConfig, CACHE_CONFIG)) {
        const cachedResponse = await getCachedResponse(
          originalRequestConfig,
          CACHE_CONFIG,
        );
        if (cachedResponse) {
          console.log(`[OCR Cache] markEdu 缓存命中: ${config.actionList[0]}`);
          if (myFlowId !== flowIdRef.current) {
            console.warn('[切题调试] markEdu 缓存命中但流程已过期，丢弃', {
              myFlowId,
              current: flowIdRef.current,
            });
            return;
          }
          setRequest(
            useList ? { ..._params, ...dataParams } : { ..._params, [_image.prefix]: _image.url }
          );
          successHandler(cachedResponse, {
            config,
            requestParams: { image: _image, requestParams: _params },
            uploadImglist: uploadImgList,
            resSubmit: cachedResponse, // 使用缓存数据作为 resSubmit
            pdfNum: _params.PdfPageNumber,
            originalRequestConfig,
            fromCache: true, // 标记为缓存命中，避免重复存储
            flowId: myFlowId,
          });
          return;
        }
      }

      const resSubmit = await baseOcrRequest({
        ...originalRequestConfig,
        skipCacheStore: true, // 提交请求不缓存，等轮询成功后统一缓存
      });

      // 提交期间用户已切走则本次作废，避免抢占共享轮询实例、污染当前状态
      if (myFlowId !== flowIdRef.current) {
        console.warn('[切题调试] markEdu 提交返回但流程已过期，放弃轮询', {
          myFlowId,
          current: flowIdRef.current,
        });
        return;
      }

      handleAddRecentMenu(config.actionList[0]);
      onImageError(resSubmit.isImageError || false);

      if (resSubmit.error || !resSubmit.JobId) {
        setIsLoading(false);
        setEduMarkData(resSubmit);
        errorHandler(resSubmit, {
          config,
          uploadImglist: uploadImgList,
          requestParams: { image: _image, requestParams: _params },
          auditData: resSubmit.auditData,
          flowId: myFlowId,
        });
        setRequest(
          useList ? { ..._params, ...dataParams } : { ..._params, [_image.prefix]: _image.url }
        );
      } else {
        const startPayload = {
          cmd: config.actionList[1],
          data: { JobId: resSubmit.JobId },
          isExample: _image.isExample,
          fileType: _image.fileType,
          module: config.module || "ocr",
        };

        const rect = dealEduAgentRect(resSubmit, false, _params.PdfPageNumber);

        console.log(
          "[markEdu] 第一阶段 (Submit) rsp:",
          JSON.stringify(
            {
              Angle: resSubmit?.QuestionInfo?.[0]?.Angle,
              Width: resSubmit?.QuestionInfo?.[0]?.Width,
              Height: resSubmit?.QuestionInfo?.[0]?.Height,
              OrgWidth: resSubmit?.OrgWidth,
              OrgHeight: resSubmit?.OrgHeight,
              hasBase64: !!resSubmit?.QuestionInfo?.[0]?.ImageBase64,
            },
            null,
            2,
          ),
        );

        onResult &&
          onResult({
            uploadImglist: uploadImgList,
            config: { ...detail, action: detail.actionList[0].action },
            image: _image,
            requestParams: _params,
            rsp: resSubmit,
            select: rect,
            rect: rect,
          });

        // 设置 eduMarkData 时，移除可能存在的旧 MarkInfos，确保 loading 能正常显示
        // 只保留 QuestionInfo 等基础数据，MarkInfos 在轮询成功后才设置
        const submitDataWithoutMark = { ...resSubmit };
        delete submitDataWithoutMark.MarkInfos;
        if (submitDataWithoutMark.data?.Response) {
          submitDataWithoutMark.data = {
            ...submitDataWithoutMark.data,
            Response: { ...submitDataWithoutMark.data.Response },
          };
          delete submitDataWithoutMark.data.Response.MarkInfos;
        }
        setEduMarkData(submitDataWithoutMark);

        // 先设置 isMarkLoading，再取消 isLoading，避免渲染时 loading 状态不一致
        setIsMarkLoading(true);
        setIsLoading(false);
        setRequest(
          useList ? { ..._params, ...dataParams } : { ..._params, [_image.prefix]: _image.url }
        );

        start(startPayload, {
          config,
          requestParams: { image: _image, requestParams: _params },
          resSubmit,
          uploadImglist: uploadImgList,
          auditData: resSubmit.auditData,
          pdfNum: _params.PdfPageNumber,
          originalRequestConfig, // 传递原始请求配置用于缓存
          flowId: myFlowId,
        });
      }
    } else {
      setIsLoading(true);

      // 根据 EnableImageCrop 参数决定是否先调用图像切边增强
      // 仅试卷切题(QuestionSplitOCR)和试卷切题仅检测(QuestionSplitLayoutOCR)需要前端调用切边增强
      const CROP_ENHANCE_ACTIONS = ['QuestionSplitOCR', 'QuestionSplitLayoutOCR'];
      const needCropEnhance = CROP_ENHANCE_ACTIONS.includes(config.action);
      let finalImage = _image;
      const payload = { ..._params };

      // MultimodalDocParse: FileType 为自动识别(0)时，根据文件后缀自动映射
      if (config.action === 'MultimodalDocParse' && (!payload.FileType || payload.FileType === 0)) {
        const FILE_TYPE_MAP = {
          pdf: 1,
          doc: 2,
          docx: 2,
          ppt: 3,
          pptx: 3,
          xls: 4,
          xlsx: 4,
          md: 5,
          txt: 6,
          jpg: 7,
          jpeg: 7,
          png: 7,
          bmp: 7,
        };
        const ext = (_image.fileType || '').toLowerCase();
        payload.FileType = FILE_TYPE_MAP[ext] || 7;
      }

      // RecognizeAgent 接口参数处理
      if (config.action === 'RecognizeAgent') {
        // QueryType=0 时不发送 Query
        if (payload.QueryType === 0) {
          delete payload.Query;
        }
        // QueryType=4 时不发送 Query，使用 SchemaItems
        if (payload.QueryType === 4) {
          delete payload.Query;
          // 规范化 SchemaItems：过滤空字段 + 根据子字段是否为空动态设置 KeyType
          payload.SchemaItems = buildSchemaItemsForRequest(payload.SchemaItems || []);
        } else {
          // 非 QueryType=4 时不发送 SchemaItems 和 EnableCoord
          delete payload.SchemaItems;
          delete payload.EnableCoord;
        }
        // 仅 PDF 文件时发送 PdfPageNumber
        if (_image.fileType !== 'pdf') {
          delete payload.PdfPageNumber;
        }
        // IsPdf 是前端控制字段，不发送到后端
        delete payload.IsPdf;
      }

      if (needCropEnhance) {
        const enableImageCrop = payload.EnableImageCrop;
        // 白名单内的接口：从 payload 中移除 EnableImageCrop，不发送到后端
        delete payload.EnableImageCrop;

        if (enableImageCrop) {
          try {
            console.log('[sync] 开启EnableImageCrop');
            const enhanceRes = await baseOcrRequest({
              cmd: 'CropEnhanceImageOCR',
              data: {
                [_image.prefix]: _image.param ? _image.param : _image.url,
                Crop: 1,           // 开启切边
                Deskew: 1,         // 开启弯曲矫正
                OnlyPosition: 0,   // 返回处理后的图片和坐标
                EnhanceType: -1,   // 不处理增强（保持原色）
                AdjustOrientation: 1 // 调整方向
              },
              isExample: false,    // CropEnhanceImageOCR 是通用接口，不是示例图片请求
              fileType: _image.fileType,
              module: config.module || 'ocr'
            });

            if (!enhanceRes.error && (enhanceRes.CroppedImageUrl || enhanceRes.CroppedImage)) {
              if (enhanceRes.CroppedImageUrl) {
                finalImage = {
                  ..._image,
                  url: enhanceRes.CroppedImageUrl,
                  previewUrl: enhanceRes.CroppedImageUrl,
                  prefix: 'ImageUrl',
                  isExample: false,
                };
              } else {
                finalImage = {
                  ..._image,
                  url: `data:image/png;base64,${enhanceRes.CroppedImage}`,
                  previewUrl: `data:image/png;base64,${enhanceRes.CroppedImage}`,
                  prefix: 'ImageBase64',
                  isExample: false,
                };
              }
            } else {
              console.warn('[sync] 图像增强失败');
            }
          } catch (e) {
            console.error('[sync] CropEnhanceImageOCR error:', e);
          }
        } else {
          console.log('[sync] EnableImageCrop=false');
        }
      }

      // 对需要 FileUrl 的接口（如 MultimodalDocParse），先将 Base64 上传到 COS 获取 URL
      if (needCosUpload(config.action) && finalImage.prefix?.includes('Base64')) {
        try {
          const fileData = finalImage.param ? finalImage.param : finalImage.url;
          const cosUrl = await uploadToCos({
            base64: fileData,
            fileName: `upload.${finalImage.fileType || 'pdf'}`,
          });
          // 替换为 URL 方式
          finalImage = {
            ...finalImage,
            url: cosUrl,
            param: '',
            prefix: finalImage.prefix.replace('Base64', 'Url'),
          };
        } catch (cosErr) {
          console.error('[COS Upload] 上传失败:', cosErr);
          setIsLoading(false);
          showErrorMessage('文件上传失败，请尝试使用在线URL方式');
          return;
        }
      }

      const res = await baseOcrRequest({
        cmd: config.action,
        data: {
          ...payload,
          [finalImage.prefix]: finalImage.param ? finalImage.param : finalImage.url,
        },
        isExample: finalImage.isExample,
        fileType: finalImage.fileType,
        module: config.module || "ocr",
        paramsList: config.paramsList,
      });

      handleAddRecentMenu(config.action);
      onImageError(res.isImageError || false);
      // RecognizeAgent 使用清理后的 payload 展示在 Request 面板
      if (config.action === 'RecognizeAgent') {
        setRequest({ ...payload, [finalImage.prefix]: _image.url });
      } else {
        setRequest({ ..._params, [_image.prefix]: _image.url });
      }

      if (res.error) {
        errorHandler(res, {
          config,
          uploadImglist: uploadImgList,
          requestParams: { image: _image, requestParams: _params },
          auditData: res.auditData,
        });
      } else {
        if (config.isImageResult) {
          const imageField = config.resultConfig?.imageResultField || "Image";
          onChangeImageEnhancement &&
            onChangeImageEnhancement({
              type: _params.TaskType === 1 ? "col" : "split",
              url: res[imageField],
            });
        }
        successHandler(res, {
          config,
          requestParams: { image: _image, requestParams: _params },
          auditData: res.auditData,
          uploadImglist: uploadImgList,
        });
      }
    }
  };

  const handleAddSmart = (type) => {
    const _key = uuidv4();
    addSmartKey({
      isCustom: type !== "custom",
      showClose: true,
      type: type === "custom" ? "input" : "select",
      key: _key,
      default: "",
      value: "",
      status: "",
    });
  };

  const handleDelSmart = (_, index) => {
    removeSmartKey(index);
  };

  const validate = (_smartKeyList = smartKeyList) => {
    const result = _smartKeyList.map((item) => {
      if (REGEX.WHITESPACE.test(item.value)) {
        return { ...item, status: "error" };
      } else {
        return { ...item, status: "" };
      }
    });
    setSmartKeyList([...result]);
    return result.filter((i) => i.status === "error").length === 0;
  };

  return (
    <div className="result-panel">
      {paramsList?.filter((i) => !i.isHide)?.length > 0 && (
        <div
          className={`result-panel-params__container ${showParams ? "open" : "close"}`}
          ref={paramsRef}
          style={
            showParams && paramsPanelHeight !== null
              ? { height: `${paramsPanelHeight}px`, maxHeight: "none" }
              : undefined
          }
        >
          <div className="result-panel-params__content">
            <header className="ocrDemo-result__panel-hd cantOpen">
              <div className="ocrDemo-result-title">
                <span className="ocrDemo-result-title__icon"></span>
                <span className="ocrDemo-result-title__text">
                  {detail?.config?.ArgumentText || "参数调整"}
                </span>
                <span
                  className="ocrDemo-result-title__reset"
                  onClick={handleResetParams}
                >
                  <span className="ocrDemo-icon ocrDemo-icon_reset"></span>
                  恢复默认
                </span>
              </div>
              <div
                className="unv-collapse__panel-collops"
                onClick={() => setShowParams(!showParams)}
              ></div>
            </header>
            <div
              className="ocrDemo-result-from-container unv-form unv-form--split"
              style={{ padding: "0 20px" }}
            >
              {/* 模板栏 - 仅在有 inputTag 类型参数时显示（ExtractDocMulti 不显示模板栏） */}
              {paramsList?.some((p) => p.type === 'inputTag') && detail?.action !== 'ExtractDocMulti' && (
                <TemplateBar
                  actionName={detail?.action || detail?.actionList?.[0]}
                  fields={{ paramsValue, apiType }}
                  fieldCount={(paramsValue[paramsList.find((p) => p.type === 'inputTag')?.key] || []).length}
                  onApplyTemplate={(saved) => {
                    if (saved.paramsValue) {
                      setParamsValue(saved.paramsValue);
                      setRequestParamsValue(saved.paramsValue);
                    }
                    if (saved.apiType && detail.syncConfig) {
                      setApiType(saved.apiType);
                      // 切换 apiType 后需要更新 paramsList
                      if (saved.apiType === 'sync') {
                        setParamsList(detail.syncConfig.paramsList);
                      } else if (saved.apiType === 'async' && detail.asyncConfig) {
                        setParamsList(detail.asyncConfig.paramsList);
                      }
                    }
                  }}
                />
              )}
              {detail.syncConfig && (
                <Form labelAlign="left">
                  <Form.Item
                    className="row"
                    label={detail.syncConfig.asyncLabel}
                  >
                    <Radio.Group
                      value={apiType}
                      onChange={(e) => setParamsAndValue(e, true, true)}
                    >
                      <Radio key="sync" value="sync">
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <span>实时模型</span>
                          {detail.syncConfig.asyncDes && (
                            <Tooltip
                              content={detail.syncConfig.asyncDes}
                              theme="light"
                            >
                              <span className="ocrDemo-icon-question"></span>
                            </Tooltip>
                          )}
                        </div>
                      </Radio>
                      <Radio key="async" value="async">
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <span>异步模型</span>
                          {detail.syncConfig.asyncDes && (
                            <Tooltip
                              content={detail.asyncConfig.asyncDes}
                              theme="light"
                            >
                              <span className="ocrDemo-icon-question"></span>
                            </Tooltip>
                          )}
                        </div>
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                </Form>
              )}

              <Form labelAlign={detail?.config?.LabelAlign || "left"}>
                {paramsList
                  ?.filter(
                    (i) =>
                      (!i.isHide || (i.showOnlyForPdf && image?.fileType === 'pdf')) &&
                      isParamsVisible(i)
                  )
                  ?.map((params) => {
                    // 获取高级配置的值（如果配置了 advancedConfigKey）
                    const advancedConfigValue = params.advancedConfigKey
                      ? paramsValue[params.advancedConfigKey]
                      : false;
                    return (
                      params.key in paramsValue && (
                        <InputParams
                          clipImg={clipImg}
                          rectOptions={rectOptions}
                          key={params.key}
                          params={{
                            ...params,
                            min:
                              typeof params.min === "string"
                                ? paramsValue[params.min]
                                : params.min || 1,
                            max:
                              typeof params.max === "string"
                                ? paramsValue[params.max]
                                : totalPage > 1 ? totalPage : undefined,
                          }}
                          value={paramsValue[params.key]}
                          showClose={params.showClose}
                          onSelect={onSelect}
                          allParamsValue={paramsValue}
                          isPdf={image?.fileType === 'pdf'}
                          advancedConfigValue={advancedConfigValue}
                          disabled={isParamsDisabled(params)}
                          onParamsChange={(updatedFields) => {
                            const _paramsValue = { ...paramsValue, ...updatedFields };
                            setParamsValue(_paramsValue);
                            setRequestParamsValue({ ...requestParamsValue, ...updatedFields });
                          }}
                          onChange={(val, realVal, extra) => {
                            const _paramsValue = {
                              ...paramsValue,
                              [params.key]: val,
                            };
                            // realVal 可能为 false / 0 / ''，需按 undefined 判断而非 ||
                            let _requestVal = {
                              ...paramsValue,
                              [params.key]: realVal !== undefined ? realVal : val,
                            };
                            
                            // 特殊处理：清除之前由 defaultConfig 添加的参数
                            // 这用于处理 GeneralAccurateOCR 中"仅识别手写体"和"仅识别印刷体"等特殊选项
                            const allDefaultConfigKeys = new Set();
                            params.options?.forEach((opt) => {
                              if (opt.defaultConfig) {
                                Object.keys(opt.defaultConfig).forEach((key) => {
                                  if (key !== params.key) {
                                    allDefaultConfigKeys.add(key);
                                  }
                                });
                              }
                            });
                            allDefaultConfigKeys.forEach((key) => {
                              delete _requestVal[key];
                            });
                            
                            // 合并新的 defaultConfig
                            if (extra?.defaultConfig) {
                              _requestVal = { ..._requestVal, ...extra.defaultConfig };
                            }

                            // ExtractDocMulti：切换 ConfigId 时，自动填充模板字段到 NewItemNames
                            if (detail?.action === 'ExtractDocMulti' && params.key === 'ConfigId') {
                              const configId = val;
                              if (configId !== 'General' && docExtractTemplates[configId]) {
                                const tpl = docExtractTemplates[configId];
                                const normalFields = Object.entries(tpl['普通字段'] || {}).map(([name]) => ({
                                  KeyName: name,
                                  KeyType: 0,
                                }));
                                const tableFields = Object.entries(tpl['表格字段'] || {}).map(([name]) => ({
                                  KeyName: name,
                                  KeyType: 1,
                                }));
                                const autoFields = [...normalFields, ...tableFields];
                                _paramsValue.NewItemNames = autoFields;
                                _requestVal.NewItemNames = autoFields;
                              } else if (configId === 'General') {
                                // 切回通用时清空自定义字段
                                _paramsValue.NewItemNames = [];
                                _requestVal.NewItemNames = [];
                              }
                            }
                            
                            // 示例规则回填：同步预设附带的关联配置（如「结合原图推理」）
                            if (extra?.presetConfig) {
                              Object.assign(_paramsValue, extra.presetConfig);
                              Object.assign(_requestVal, extra.presetConfig);
                            }

                            setParamsValue(_paramsValue);
                            setRequestParamsValue(_requestVal);
                            
                            if (extra?.startImmediately && image.isExample) {
                              // 选项配置了 defaultImg 时切换到对应示例图；
                              // 未配置时（如场景鉴伪的最新配置，示例图由 imglist 的 defaultConfig 决定）
                              // 不能拼出 COS_URL + undefined，否则请求的是 .../undefined 会被后端拒绝。
                              // 此时沿用当前示例图，按新参数重新识别。
                              const defaultImg = extra.defaultImg
                                ? COS_URL + extra.defaultImg
                                : image.url;
                              onChangeImg &&
                                onChangeImg(defaultImg, _requestVal);
                            }
                          }}
                        />
                      )
                    );
                  })}
                {smartKeyList
                  .filter((i) => !i.isHide)
                  ?.map((params, index) => {
                    return (
                      <InputParams
                        key={params.key}
                        params={{
                          ...params,
                          label: params.label || `字段${index + 1}`,
                          options: params.isCustom
                            ? detail?.options?.map((item, idx) => ({
                                text: item,
                                value: idx,
                              }))
                            : params.options,
                        }}
                        status={params.status}
                        placeholder={params.placeholder}
                        value={params.value}
                        showClose={params.showClose}
                        onClose={() => handleDelSmart(params, index)}
                        onChange={(val) => {
                          updateSmartKey(params.key, {
                            value: val,
                            status: REGEX.WHITESPACE.test(val) ? "error" : "",
                          });
                        }}
                      />
                    );
                  })}
                {(detail?.showCustomItem ||
                  detail?.resultConfig?.showCustomItem) && detail?.action !== 'ExtractDocMulti' && (
                  <p style={{ marginTop: "8px", marginBottom: "20px" }}>
                    <Button
                      theme="default"
                      variant="outline"
                      disabled={isLoading}
                      onClick={() => handleAddSmart("default")}
                    >
                      <span className="ocrDemo-icon_add"></span>
                      <span>默认key</span>
                    </Button>
                    <Button
                      theme="default"
                      variant="outline"
                      disabled={isLoading}
                      onClick={() => handleAddSmart("custom")}
                      style={{ marginLeft: "20px" }}
                    >
                      <span className="ocrDemo-icon_add"></span>
                      <span>自定义key</span>
                    </Button>
                  </p>
                )}
                <div
                  className="message_scroll__bar"
                  style={{ clear: "both", height: "1px", width: "100%" }}
                  ref={messagesEnd}
                ></div>
              </Form>
            </div>
          </div>
        </div>
      )}
      {/* 参数面板和响应面板之间的拖拽分隔条 */}
      {paramsList?.filter((i) => !i.isHide)?.length > 0 && showParams && (
        <div
          className={`result-panel-resizer ${isResizing ? "resizing" : ""}`}
          onMouseDown={handleResizeStart}
          title="拖拽调整参数面板高度"
        >
          <div className="result-panel-resizer__handle" />
        </div>
      )}
      <div className="ocrDemo-result-response-container">
        <Tabs
          className="ocrDemo-result-response__tab"
          defaultValue={currentTab}
          value={currentTab}
          onChange={(value) => setCurrentTab(value)}
        >
          {rspTabs.map((item) => (
            <Tabs.TabPanel value={item.id} label={item.label} key={item.id}>
              <ResponseParams type={item.type} onSelect={onSelect} />
            </Tabs.TabPanel>
          ))}
        </Tabs>
        <footer className="unv-collapse__panel__footer">
          <div className="ocrDemo-footer-btn-container">
            {detail?.showDownBtn && (
              <>
                {request[detail.resultConfig?.markdownKey] === "Mdbase64" && (
                  <Button
                    theme="default"
                    variant="outline"
                    onClick={() =>
                      DownloadMd(
                        response[detail.resultConfig?.markdownContent],
                        "md",
                        "结果",
                      )
                    }
                  >
                    导出Markdown结果
                  </Button>
                )}
                {request[detail.resultConfig?.markdownKey] ===
                  "Excelbase64" && (
                  <Button
                    theme="default"
                    variant="outline"
                    onClick={() =>
                      DownloadExcel(
                        response[detail.resultConfig?.markdownContent],
                        "结果",
                      )
                    }
                  >
                    导出Excel结果
                  </Button>
                )}
              </>
            )}
            {detail?.action === "RecognizeAgent" && response && !response.error && JSON.stringify(response) !== '{}' && (
              <Button
                theme="default"
                variant="outline"
                onClick={() => DownloadJson(response, "识别结果")}
              >
                导出JSON
              </Button>
            )}
            {detail?.actionType === "Async" ||
            apiType === "async" ||
            detail?.actionType === "markEdu" ? (
              <div
                className="ocrDemo-result__btn"
                onClick={() => {
                  if (isThinking || isMarkLoading) {
                    handleStop();
                  } else {
                    if (isLoading) return;
                    handleStart({ _params: requestParamsValue });
                  }
                }}
              >
                <div
                  className={`ocrDemo-result__btn_content ${isThinking || isMarkLoading ? "stop" : "start"}`}
                ></div>
                <div className="ocrDemo-result__btn_border"></div>
              </div>
            ) : detail?.actionType === "eduAgent" ? (
              <div
                className="ocrDemo-result__btn"
                onClick={() => handleStartEduAgent({ clipImg })}
              >
                <div className="ocrDemo-result__btn_content start"></div>
                <div className="ocrDemo-result__btn_border"></div>
              </div>
            ) : (
              <div
                className="ocrDemo-result__btn"
                onClick={() => handleStart({ _params: requestParamsValue })}
              >
                <div className="ocrDemo-result__btn_content"></div>
                <div className="ocrDemo-result__btn_border"></div>
              </div>
            )}
          </div>
        </footer>
      </div>

      {/* 文档抽取多模态版欢迎引导弹窗 */}
      <DocExtractWelcomeDialog
        visible={welcomeDialogVisible}
        onClose={() => setWelcomeDialogVisible(false)}
      />
    </div>
  );
}
